/**
 * InflowNode - Income source node component
 *
 * Renders an inflow (income) node with light background and border.
 * Height is proportional to the income value.
 *
 * WP-FLOW-6: Updated to use light background + border styling
 */

import { memo, useState, useCallback } from 'react';
import type { InflowNode as InflowNodeType, NodeProps } from '../types';
import { NODE, COLORS, A11Y } from '../constants';
import { formatCurrency } from '@/utils/format';

export interface InflowNodeProps extends NodeProps<InflowNodeType> {
  /** Called when mouse enters the node */
  onMouseEnter?: (node: InflowNodeType, event: React.MouseEvent) => void;
  /** Called when mouse leaves the node */
  onMouseLeave?: (node: InflowNodeType) => void;
  /** Whether this node is currently highlighted */
  isHighlighted?: boolean;
}

function InflowNodeComponent({
  node,
  x,
  y,
  width,
  height,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isHighlighted = false,
}: InflowNodeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleClick = useCallback(() => {
    onClick?.(node);
  }, [onClick, node]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.(node);
      }
    },
    [onClick, node]
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      setIsHovered(true);
      onMouseEnter?.(node, e);
    },
    [node, onMouseEnter]
  );

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onMouseLeave?.(node);
  }, [node, onMouseLeave]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  // Get inflow colors (light green with border)
  const nodeColors = COLORS.inflow;

  // Calculate label positions - labels on the LEFT of node (outside flow lane)
  // WP-FLOW-7: Adjusted from -10 to -8 for closer positioning to narrow bar
  const labelX = x - 8;
  const labelY = y + height / 2;

  // Visual state
  const showHighlight = isHovered || isFocused || isHighlighted;

  return (
    <g
      className="inflow-node"
      role="button"
      tabIndex={0}
      aria-label={`Income: ${node.name}, ${formatCurrency(node.value)}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Focus ring (rendered behind node) */}
      {isFocused && (
        <rect
          x={x - 3}
          y={y - 3}
          width={width + 6}
          height={height + 6}
          rx={NODE.BORDER_RADIUS + 2}
          ry={NODE.BORDER_RADIUS + 2}
          fill="none"
          stroke={A11Y.FOCUS_RING.split(' ')[2]}
          strokeWidth={2}
        />
      )}

      {/* Node rectangle with light background and border */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={NODE.BORDER_RADIUS}
        ry={NODE.BORDER_RADIUS}
        fill={nodeColors.background}
        stroke={nodeColors.border}
        strokeWidth={NODE.BORDER_WIDTH}
        opacity={showHighlight ? 1 : 0.95}
        filter={showHighlight ? 'url(#node-shadow-hover)' : 'url(#node-shadow)'}
        className="transition-all duration-150 ease-in-out"
      />

      {/* Hover highlight effect */}
      {showHighlight && (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={NODE.BORDER_RADIUS}
          ry={NODE.BORDER_RADIUS}
          fill={nodeColors.border}
          opacity={0.1}
          className="pointer-events-none"
        />
      )}

      {/* Name label - right-aligned to be near the node */}
      <text
        x={labelX}
        y={labelY - 6}
        textAnchor="end"
        className="sankey-label"
        fill={COLORS.text.primary}
        fontSize={12}
        fontWeight={showHighlight ? 600 : 500}
      >
        {node.name}
      </text>

      {/* Amount label */}
      <text
        x={labelX}
        y={labelY + 12}
        textAnchor="end"
        className="sankey-amount"
        fill={COLORS.text.secondary}
        fontSize={11}
      >
        {formatCurrency(node.value)}
      </text>
    </g>
  );
}

export const InflowNode = memo(InflowNodeComponent);
export default InflowNode;
